import React, { useState } from "react";
import { Card, CardContent, CardTitle } from "../../misc/Card";
import { Button } from "../../misc/Button";
import { OpenhousePostProps } from "./types";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import CustomDialog from "../../misc/CustomDialog";
import { X } from "lucide-react";

const OpenHousePosts: React.FC<OpenhousePostProps> = ({
    listing,
    setIsOpen,
}) => {
    const isSyncing = listing?.isSyncing || false;
    const [showMLSOpenHouseDialog, setShowMLSOpenHouseDialog] = useState(false);

    const handleCreateOpenhousePosts = () => {
      if (
        !listing ||
        !listing.campaignId ||
        listing.campaignStatus?.toLowerCase() === "sold"
      ) {
        toast.error(
          <CustomToast
            message="Your listing is either sold or does not have an active campaign."
            type="error"
          />,
          { autoClose: 3000 },
        );
        return;
      }

      if (
        !listing ||
        !listing.campaignId ||
        listing.campaignStatus?.toLowerCase() !== "active"
      ) {
        toast.error(
          <CustomToast
            message="Please create a campaign first."
            type="error"
          />,
          { autoClose: 3000 },
        );
        return;
      }
      // Check if MLS Open House Sync is enabled
      if (
        listing?.listingType === "MLSListing" &&
        listing?.campaigns?.openHouseMonitoring === true
      ) {
        setShowMLSOpenHouseDialog(true);
        return;
      }

      // If not MLS listing or MLS Open House Sync is not enabled, proceed
      setIsOpen(true);
    };

    const handleCancelDialog = () => {
        setShowMLSOpenHouseDialog(false);
    };

    const handleContinueDialog = () => {
        setShowMLSOpenHouseDialog(false);
        setIsOpen(true);
    };

    return (
        <>
            <div className="relative">
                <Card className="mt-0">
                    <CardContent className="pt-6">
                        <CardTitle className="mb-4">Open House Posts</CardTitle>
                        <div className="flex flex-col justify-center">
                            <Button
                                variant="secondary"
                                onClick={handleCreateOpenhousePosts}
                                disabled={isSyncing}
                            >
                                Create Open House Posts
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* MLS Open House Sync Dialog */}
            <CustomDialog
                open={showMLSOpenHouseDialog}
                onClose={() => setShowMLSOpenHouseDialog(false)}
                className="max-w-4xl w-full"
            >
                <div className="max-w-4xl min-w-4xl bg-white">
                    <h2 className="text-xl font-bold mb-4 text-primary text-center">
                        MLS Open House Sync
                    </h2>
                    <button
                        className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
                        onClick={() => setShowMLSOpenHouseDialog(false)}
                    >
                        <X className="h-6 w-6" />
                    </button>
                    <div className="space-y-4">
                        <p className="text-primary font-semibold text-lg">
                            How open houses are selected:
                        </p>
                        <p className="text-gray-700 text-sm">
                            Marabot looks for open houses scheduled within 10
                            days of your post date. If no open houses are found
                            in that timeframe, your post won't include any open
                            house details.
                        </p>
                        <p className="text-gray-700 text-sm">
                            Once an open house is within range, Marabot will
                            automatically include up to 4 upcoming open
                            houses—regardless of their specific dates. To ensure
                            full weekend coverage, if the last open house falls
                            on a Saturday and there's also one scheduled for
                            Sunday, both will be included, allowing for a
                            maximum of 5.
                        </p>
                        <p className="text-gray-700 text-sm">
                            When open house details are added, Marabot will also
                            update the call to action and move it to the top of
                            the post—regardless of the call to action location
                            you originally selected.
                        </p>
                        <p className="text-gray-700 text-sm font-semibold mt-4">
                            Are you sure you wish to continue?
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 mt-6">
                        <Button onClick={handleContinueDialog}>
                            Continue
                        </Button>
                        <Button variant="destructive" onClick={handleCancelDialog}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </CustomDialog>
        </>
    );
};

export default OpenHousePosts;
